import { Component, inject, OnInit, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import { tryGetErrorMessage } from '../../../../../error.utils';
import { copyToClipboard } from '../../../../../public/util/navigator.utils';
import { AssignmentService } from '../../../../../service/assignment.service';
import { LossPreventionService } from '../../../../../service/loss-prevention.service';
import { NotifyService } from '../../../../../service/notify.service';
import { PanoptycAdminService } from '../../../../../service/panoptyc-admin.service';
import { WalkoutService } from '../../../../../service/walkout.service';
import { LPLocation, ReviewSubjectType } from '../../../../../types';

@Component({
  selector: 'app-loss-prevention-admin-sideload-review',
  templateUrl: './sideload-review.component.html',
  styleUrl: './sideload-review.component.scss',
  standalone: false,
})
export class LossPreventionAdminSideloadReviewComponent implements OnInit {
  private adminService = inject(PanoptycAdminService);
  private assignmentService = inject(AssignmentService);
  private lpService = inject(LossPreventionService);
  private walkoutService = inject(WalkoutService);
  private notifyService = inject(NotifyService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  locationControl = new FormControl();
  locationOptions: Observable<LPLocation[]>;

  reviewMessage = signal<string>('');
  reviewUrl = signal<string>('');
  errorMessage = signal<string>('');

  sideloadedReviewKind: ReviewSubjectType;
  readonly ReviewSubjectType = ReviewSubjectType; // Make enum accessible in template
  readonly reviewKinds = [
    { id: ReviewSubjectType.Cancellation, label: 'Cancellation review, add to the queue' },
    { id: ReviewSubjectType.Underringing, label: 'Underringing review, add to the queue' },
    { id: ReviewSubjectType.Deepdive, label: 'Deep dive review, add to the queue' },
    { id: ReviewSubjectType.Walkout, label: 'Walkout review, add to the queue' },
    { id: ReviewSubjectType.Sideloaded, label: 'Sideloaded, do not add to the queue' },
  ];

  isLoading = signal<boolean>(false);
  public walkoutAvailable = signal<boolean>(false);

  ngOnInit() {
    this.sideloadedReviewKind = ReviewSubjectType.Walkout;
    this.locationOptions = this.locationControl.valueChanges.pipe(
      startWith(''),
      debounceTime(200),
      distinctUntilChanged(),
      switchMap((val) => {
        return this._filter(val || '');
      }),
    );
    this.locationControl.valueChanges.subscribe(() => {
      this.checkAvailableWalkouts();
    });

    this.selectPreviousLocation();
  }

  checkAvailableWalkouts() {
    if (!this.locationControl.value?.location_id) {
      return;
    }
    this.isLoading.set(true);
    this.walkoutService.getAvailableWalkouts(this.locationControl.value?.location_id).subscribe((walkouts) => {
      this.walkoutAvailable.set(walkouts.length > 0);
      // If walkouts are not available and walkout option is selected, switch to first option
      if (!this.walkoutAvailable() && this.sideloadedReviewKind === ReviewSubjectType.Walkout) {
        this.sideloadedReviewKind = ReviewSubjectType.Cancellation;
      }
      this.isLoading.set(false);
    });
  }

  createReview() {
    this.errorMessage.set('');
    if (!this.locationControl.value?.location_id) {
      return;
    }
    this.isLoading.set(true);
    this.assignmentService
      .createAssignment(
        this.locationControl.value?.location_id,
        this.reviewMessage(),
        this.sideloadedReviewKind,
        this.sideloadedReviewKind === ReviewSubjectType.Cancellation ||
          this.sideloadedReviewKind === ReviewSubjectType.Underringing ||
          this.sideloadedReviewKind === ReviewSubjectType.Deepdive ||
          this.sideloadedReviewKind === ReviewSubjectType.Walkout,
      )
      .subscribe(
        (result) => {
          if (!result || !result.review_subject_type || !result.review_id) {
            this.errorMessage.set('Invalid review assignment received.');
            this.isLoading.set(false);
            return;
          }

          this.reviewUrl.set(window.location.href.replace(this.router.url, '') + `/lp/review/${result.review_id}`);

          this.isLoading.set(false);
        },
        (error) => {
          // i know right? don't ask me
          this.errorMessage.set(tryGetErrorMessage(error));

          this.isLoading.set(false);
        },
      );
  }

  public search(term: string, item: LPLocation) {
    return (
      item.branch_name.toLowerCase().includes(term.toLowerCase()) ||
      item.location_name.toLowerCase().includes(term.toLowerCase()) ||
      item.branch_id.toString().includes(term.toLowerCase()) ||
      item.location_id.toString().includes(term.toLowerCase())
    );
  }

  private _filter(value: string | LPLocation): Observable<LPLocation[]> {
    const filterValue = typeof value === 'string' ? value.toLowerCase() : value.location_name.toLowerCase();

    return this.adminService.getLocationsAutocomplete(filterValue).pipe(
      // map the item property of the github results as our return object
      map((results) => results),
      // catch errors
      catchError(() => {
        return of(null);
      }),
    );
  }

  public changeLocation(location: LPLocation) {
    if (!location) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParamsHandling: 'replace',
        queryParams: {},
      });
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: { location_id: location.location_id, branch_id: location.branch_id },
    });
  }

  private selectPreviousLocation() {
    const query = this.route.snapshot.queryParams;
    const { location_id } = query;
    if (!location_id) return;

    this.adminService.getLocationsAutocomplete('').subscribe((d) => {
      this.locationControl.setValue(d.find((l) => l.location_id === +location_id));
    });
  }

  public copyUrl() {
    copyToClipboard(this.reviewUrl());
    this.notifyService.notify('Copied to clipboard!');
  }
}
